import { useEffect as useEffectE, useMemo as useMemoE, useState as useStateE } from "react";
import { Api } from "./api";
import { Banner, Field, Icon } from "./components";

// Community calendar — homeowner reads, manager+ creates/edits/deletes.

// Effective role -> can manage events?
//
// `effectiveRole` is the UI role currently driving the SPA's view — set
// by the TopBar role-switcher (state lives in app.jsx as `auth`, values
// are "homeowner"/"manager"/"admin" per Api.uiRole). A manager using
// "View as Homeowner" must see homeowner-grade UI on shared views like
// Calendar; reading `user.role` here would surface Add/Edit/Delete
// buttons that contradict the preview. Backend authority is unchanged —
// this is purely a render-side guard.
function canManageEvents(effectiveRole) {
  return effectiveRole === "manager" || effectiveRole === "admin";
}

const _MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const _WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function _ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function _sameYmd(a, b) { return _ymd(a) === _ymd(b); }
function _fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function _fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
// HTML <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in *local* time.
function _toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function _fromLocalInput(s) {
  // datetime-local has no zone; the browser interprets it as local. new Date() does that too.
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function CommunityCalendar({ user, role }) {
  const today = new Date();
  const [view, setView] = useStateE({ year: today.getFullYear(), month: today.getMonth() });
  const [events, setEvents] = useStateE({ loading: true, error: null, list: [] });
  const [selectedDay, setSelectedDay] = useStateE(null); // Date or null
  const [editing, setEditing] = useStateE(null); // null | "new" | event obj
  const [reloadKey, setReloadKey] = useStateE(0);

  const isManager = canManageEvents(role);
  const monthStart = useMemoE(() => new Date(view.year, view.month, 1), [view.year, view.month]);
  const monthEnd = useMemoE(() => new Date(view.year, view.month + 1, 1), [view.year, view.month]);

  useEffectE(() => {
    let cancelled = false;
    setEvents({ loading: true, error: null, list: [] });
    // Pad the window by a week each side so we can show carry-over for the
    // 6-row grid (a few days from prev/next month).
    const from = new Date(monthStart); from.setDate(from.getDate() - 7);
    const to = new Date(monthEnd); to.setDate(to.getDate() + 7);
    Api.eventsList({ from: from.toISOString(), to: to.toISOString() })
      .then((list) => { if (!cancelled) setEvents({ loading: false, error: null, list: list || [] }); })
      .catch((err) => { if (!cancelled) setEvents({ loading: false, error: err.message || "Couldn't load events.", list: [] }); });
    return () => { cancelled = true; };
  }, [monthStart, monthEnd, reloadKey]);

  const eventsByYmd = useMemoE(() => {
    const map = new Map();
    for (const e of events.list) {
      const k = _ymd(new Date(e.starts_at));
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
    }
    return map;
  }, [events.list]);

  const goto = (deltaMonths) => {
    const d = new Date(view.year, view.month + deltaMonths, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };
  const gotoToday = () => {
    setView({ year: today.getFullYear(), month: today.getMonth() });
    setSelectedDay(today);
  };

  const dayEvents = selectedDay ? (eventsByYmd.get(_ymd(selectedDay)) || []) : [];

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 1100 }}>
        <div className="row" style={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <button className="btn btn--ghost btn--sm" onClick={() => goto(-1)} aria-label="Previous month">
              <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} />
            </button>
            <h2 className="display" style={{ fontSize: 22, fontWeight: 500, minWidth: 200 }}>
              {_MONTH[view.month]} {view.year}
            </h2>
            <button className="btn btn--ghost btn--sm" onClick={() => goto(1)} aria-label="Next month">
              <Icon name="chevron" size={14} />
            </button>
            <button className="btn btn--ghost btn--sm" onClick={gotoToday}>Today</button>
          </div>
          {/* Manager: full create authority. Homeowner: can propose
              an event; the backend routes the same POST to PENDING
              status and bells the community's managers for review.
              Surface the button to everyone so residents can suggest
              events without needing to email a manager. */}
          <button
            className="btn btn--accent btn--sm"
            onClick={() => setEditing("new")}
          >
            <Icon name="plus" size={14} /> {isManager ? "Add event" : "Propose event"}
          </button>
        </div>

        {events.error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{events.error}</div>
        )}

        <CalendarGrid
          year={view.year}
          month={view.month}
          today={today}
          selectedDay={selectedDay}
          onDayClick={setSelectedDay}
          eventsByYmd={eventsByYmd}
          loading={events.loading}
        />

        <DayEventsPanel
          day={selectedDay}
          events={dayEvents}
          canManage={isManager}
          onAdd={() => setEditing("new")}
          onEdit={(e) => setEditing(e)}
          onDelete={async (e) => {
            if (!window.confirm(`Delete "${e.title}"?`)) return;
            try {
              await Api.eventDelete(e.id);
              setReloadKey((k) => k + 1);
            } catch (err) {
              alert(err.message || "Couldn't delete event.");
            }
          }}
        />

        {editing && (
          <EventEditor
            initial={editing === "new" ? null : editing}
            defaultDay={selectedDay || today}
            isManager={isManager}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); setReloadKey((k) => k + 1); }}
          />
        )}
      </div>
    </div>
  );
}

function CalendarGrid({ year, month, today, selectedDay, onDayClick, eventsByYmd, loading }) {
  // Build 6 rows × 7 cols starting from the Sunday on/before the 1st.
  const first = new Date(year, month, 1);
  const startOffset = first.getDay();
  const gridStart = new Date(year, month, 1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
        background: "var(--bg-2)", borderBottom: "1px solid var(--hairline)",
      }}>
        {_WEEKDAY_SHORT.map((w) => (
          <div key={w} style={{
            padding: "8px 10px", fontSize: 12, fontWeight: 600,
            color: "var(--mute)", textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}>{w}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === month;
          const isToday = _sameYmd(d, today);
          const isSelected = selectedDay && _sameYmd(d, selectedDay);
          const dayEvents = eventsByYmd.get(_ymd(d)) || [];
          return (
            <button
              key={i}
              onClick={() => onDayClick(d)}
              style={{
                // minWidth:0 is the CSS Grid 'auto-track' fix: without
                // it a cell containing a nowrap event pill expands to
                // fit the full text and blows out the column 1fr
                // equality (and shoves the day-name header off screen
                // on phones). With min-width:0 the column respects 1fr
                // and the pill ellipsis-truncates cleanly.
                minWidth: 0,
                overflow: "hidden",
                minHeight: 96, padding: "6px 8px",
                textAlign: "left", cursor: "pointer",
                background: isSelected ? "var(--accent-soft)" : "var(--surface)",
                borderRight: (i % 7) === 6 ? 0 : "1px solid var(--hairline)",
                borderBottom: i < 35 ? "1px solid var(--hairline)" : 0,
                opacity: inMonth ? 1 : 0.45,
                position: "relative",
              }}
            >
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 22, height: 22, fontSize: 13, fontWeight: isToday ? 700 : 500,
                  borderRadius: 999,
                  background: isToday ? "var(--accent)" : "transparent",
                  color: isToday ? "#fff" : "var(--ink-2)",
                  padding: "0 6px",
                }}>{d.getDate()}</span>
              </div>
              <div className="col" style={{ gap: 2, marginTop: 4, minWidth: 0 }}>
                {dayEvents.slice(0, 3).map((e) => (
                  <div
                    key={e.id}
                    title={e.title}
                    style={{
                      fontSize: 11, lineHeight: 1.25, padding: "1px 6px",
                      background: "var(--accent-soft)",
                      color: "var(--accent-ink)",
                      borderRadius: 4,
                      overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0, maxWidth: "100%",
                    }}
                  >
                    <span className="mono" style={{ fontSize: 10, marginRight: 4, opacity: 0.7 }}>
                      {_fmtTime(e.starts_at)}
                    </span>
                    {e.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div style={{ fontSize: 11, color: "var(--mute)", padding: "0 6px" }}>
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {loading && (
        <div style={{ padding: 8, textAlign: "center", color: "var(--mute)", fontSize: 12 }}>
          Loading events…
        </div>
      )}
    </div>
  );
}

function DayEventsPanel({ day, events, canManage, onAdd, onEdit, onDelete }) {
  if (!day) {
    return (
      <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
        Pick a day to see its events{canManage ? " or click Add event" : ""}.
      </div>
    );
  }
  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="display" style={{ fontSize: 20, fontWeight: 500 }}>{_fmtDate(day.toISOString())}</h3>
        {canManage && (
          <button className="btn btn--ghost btn--sm" onClick={onAdd}>
            <Icon name="plus" size={13} /> Add event
          </button>
        )}
      </div>
      <hr className="divider" />
      {events.length === 0 ? (
        <div style={{ color: "var(--mute)", padding: "8px 0" }}>No events on this day.</div>
      ) : (
        <div className="col" style={{ gap: 12 }}>
          {events.map((e) => <EventCard key={e.id} event={e} canManage={canManage} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, canManage, onEdit, onDelete }) {
  const start = new Date(event.starts_at);
  const end = event.ends_at ? new Date(event.ends_at) : null;
  return (
    <div className="card" style={{ padding: 14, background: "var(--surface-2)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{event.title}</div>
          <div className="text-sm text-mute mono" style={{ marginTop: 2 }}>
            {_fmtTime(start)}{end ? ` – ${_fmtTime(end)}` : ""}
            {event.location && <> · {event.location}</>}
          </div>
          {event.description && (
            <div className="text-sm" style={{ marginTop: 8, whiteSpace: "pre-wrap", color: "var(--ink-2)" }}>
              {event.description}
            </div>
          )}
        </div>
        {canManage && (
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn--ghost btn--sm" onClick={() => onEdit(event)}>Edit</button>
            <button className="btn btn--ghost btn--sm" onClick={() => onDelete(event)} style={{ color: "var(--danger)" }}>Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EventEditor({ initial, defaultDay, isManager, onClose, onSaved }) {
  const isEditing = !!initial;
  const startDefault = initial?.starts_at
    ? new Date(initial.starts_at)
    : (() => {
        // Default: the selected day at 6pm local.
        const d = new Date(defaultDay);
        d.setHours(18, 0, 0, 0);
        return d;
      })();
  const endDefault = initial?.ends_at ? new Date(initial.ends_at) : null;

  const [title, setTitle] = useStateE(initial?.title || "");
  const [description, setDescription] = useStateE(initial?.description || "");
  const [location, setLocation] = useStateE(initial?.location || "");
  const [startsLocal, setStartsLocal] = useStateE(_toLocalInput(startDefault));
  const [endsLocal, setEndsLocal] = useStateE(endDefault ? _toLocalInput(endDefault) : "");
  const [busy, setBusy] = useStateE(false);
  const [error, setError] = useStateE(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!title.trim()) { setError("Title is required."); return; }
    const starts = _fromLocalInput(startsLocal);
    if (!starts) { setError("Pick a valid start date/time."); return; }
    let ends = null;
    if (endsLocal) {
      ends = _fromLocalInput(endsLocal);
      if (!ends) { setError("Pick a valid end date/time."); return; }
      if (ends < starts) { setError("End must be at or after start."); return; }
    }
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        starts_at: starts.toISOString(),
        ends_at: ends ? ends.toISOString() : null,
      };
      if (isEditing) {
        await Api.eventUpdate(initial.id, body);
      } else {
        await Api.eventCreate(body);
      }
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card--pad-lg" style={{ borderColor: "var(--accent)", borderStyle: "dashed" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 12 }}>
        <h3 className="display" style={{ fontSize: 20, fontWeight: 500 }}>
          {isEditing ? "Edit event" : (isManager ? "Add event" : "Propose event")}
        </h3>
        <button className="btn btn--quiet btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
      {!isEditing && !isManager && (
        <div className="text-sm text-mute" style={{ marginBottom: 10 }}>
          Your proposal goes to your community manager. Once they approve,
          the event shows up on everyone's calendar.
        </div>
      )}
      <form className="col" style={{ gap: 12 }} onSubmit={submit}>
        <Field label="Title">
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
        </Field>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Starts">
            <input className="input" type="datetime-local" value={startsLocal} onChange={(e) => setStartsLocal(e.target.value)} required />
          </Field>
          <Field label="Ends (optional)">
            <input className="input" type="datetime-local" value={endsLocal} onChange={(e) => setEndsLocal(e.target.value)} />
          </Field>
        </div>
        <Field label="Location (optional)">
          <input className="input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Clubhouse, Zoom link" />
        </Field>
        <Field label="Description (optional)">
          <textarea className="textarea" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's happening, who should come, anything else." />
        </Field>
        {error && (
          <div role="alert" style={{
            padding: "10px 12px", borderRadius: 8, fontSize: 14,
            background: "var(--danger-soft, #fdecea)",
            color: "var(--danger, #b3261e)",
            border: "1px solid var(--danger, #b3261e)",
          }}>{error}</div>
        )}
        <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--accent" disabled={busy || !title.trim()}>
            {busy
              ? "Saving…"
              : isEditing
                ? "Save changes"
                : isManager ? "Create event" : "Submit proposal"}
          </button>
        </div>
      </form>
    </div>
  );
}

export { CommunityCalendar };
